import { useLanguage } from "@/contexts/LanguageContext";
import { Sparkles, TrendingUp, Target, Zap } from "lucide-react";
import AnimatedSection from "@/components/AnimatedSection";
import geminiLogo from "@/assets/logos/gemini.png";
import chatgptLogo from "@/assets/logos/chatgpt.webp";
import perplexityLogo from "@/assets/logos/perplexity.png";

const AIVisibility = () => {
  const { t, language } = useLanguage();

  const chartData = [
    { month: "Jan", value: 45 },
    { month: "Fév", value: 52 },
    { month: "Mar", value: 48 },
    { month: "Avr", value: 58 },
    { month: "Mai", value: 65 },
    { month: "Juin", value: 62 },
  ];

  const maxValue = Math.max(...chartData.map(d => d.value));

  const topSources = [
    { name: "ChatGPT", citations: 156, models: ["GPT-4", "GPT-4o"], color: "from-slate-700 to-slate-900", logo: chatgptLogo },
    { name: "Gemini", citations: 142, models: ["Gemini 2.0"], color: "from-blue-500 to-indigo-600", logo: geminiLogo },
    { name: "Perplexity", citations: 98, models: ["Sonar"], color: "from-indigo-500 to-purple-600", logo: perplexityLogo },
  ];

  return (
    <section id="ai-visibility" className="py-10 lg:py-12 bg-slate-50/50">
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <AnimatedSection animation="fade-right">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-200/60 border border-slate-300/50 backdrop-blur-sm">
                <Zap className="h-4 w-4 text-sky-500" />
                <span className="text-sm font-medium text-sky-600">{t("visibility.badge")}</span>
              </div>

              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-tight text-slate-800">
                {t("visibility.title")}
                <br />
                <span className="bg-gradient-to-r from-sky-400 via-blue-500 to-sky-400 bg-clip-text text-transparent animate-gradient-shine">
                  {t("visibility.title.highlight")}
                </span>
              </h2>

              <p className="text-lg text-slate-500 leading-relaxed">
                {t("visibility.desc")}
              </p>

              <div className="grid sm:grid-cols-3 gap-6 pt-4">
                {[
                  { icon: Target, color: 'sky', title: t("visibility.cited.title"), desc: t("visibility.cited.desc") },
                  { icon: TrendingUp, color: 'sky', title: t("visibility.trends.title"), desc: t("visibility.trends.desc") },
                  { icon: Sparkles, color: 'sky', title: t("visibility.competition.title"), desc: t("visibility.competition.desc") },
                ].map((item, index) => (
                  <div 
                    key={index} 
                    className="group flex items-start gap-3 transform transition-all duration-300 hover:translate-x-1"
                    style={{ transitionDelay: `${index * 0.1}s` }}
                  >
                    <div className="p-2.5 rounded-xl border transition-all duration-300 group-hover:scale-110 bg-sky-100/80 border-sky-200/50 group-hover:shadow-lg group-hover:shadow-sky-500/20">
                      <item.icon className="h-5 w-5 text-sky-500" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-700">{item.title}</div>
                      <div className="text-sm text-slate-500">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>

          {/* Right Dashboard Mockup */}
          <AnimatedSection animation="fade-left" delay={0.2}>
            <div className="space-y-6">
              {/* Chart Card */}
              <div className="group relative p-6 rounded-3xl card-highlight transition-all duration-500 overflow-hidden">
                {/* Glow effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  <div className="absolute -inset-1 bg-gradient-to-r from-sky-500/15 via-blue-500/15 to-sky-500/15 blur-xl" />
                </div>
                
                {/* Glass shine effect */}
                <div className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none z-30">
                  <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine" />
                </div>
                
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-xl font-display font-bold text-slate-800 mb-1 transition-colors duration-300 group-hover:text-sky-600">
                        {t("visibility.chart.title")}
                      </h3>
                      <p className="text-sm text-slate-500">
                        {t("visibility.chart.subtitle")}
                      </p>
                    </div>
                    <div className="px-3 py-1 rounded-full bg-emerald-100/80 border border-emerald-200/50">
                      <span className="text-xs font-medium text-emerald-600">+23%</span>
                    </div>
                  </div>

                  {/* Chart */}
                  <div className="space-y-4">
                    <div className="flex items-end justify-between gap-3 h-48">
                      {chartData.map((data, index) => (
                        <div key={index} className="flex-1 flex flex-col items-center gap-2 group/bar">
                          <div className="w-full relative" style={{ height: '160px' }}>
                            <div
                              className="absolute bottom-0 w-full rounded-xl transition-all duration-500 group-hover/bar:scale-x-110"
                              style={{
                                height: `${(data.value / maxValue) * 100}%`,
                                background: `linear-gradient(180deg, rgba(14, 165, 233, 0.9), rgba(59, 130, 246, 0.7))`,
                                boxShadow: '0 0 20px rgba(14, 165, 233, 0.2)',
                              }}
                            />
                          </div>
                          <span className="text-xs text-slate-400 font-medium transition-colors duration-300 group-hover/bar:text-sky-500">
                            {data.month}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Sources Card */}
              <div className="group relative p-6 rounded-3xl card-highlight transition-all duration-500 overflow-hidden">
                {/* Glow effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  <div className="absolute -inset-1 bg-gradient-to-r from-sky-500/15 via-blue-500/15 to-sky-500/15 blur-xl" />
                </div>
                
                {/* Glass shine effect */}
                <div className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none z-30">
                  <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/25 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine" />
                </div>
                
                <div className="relative z-10">
                  <h3 className="text-lg font-display font-bold text-slate-800 mb-4 transition-colors duration-300 group-hover:text-sky-600">
                    {t("visibility.top.title")}
                  </h3>
                  <div className="space-y-3">
                    {topSources.map((source, index) => (
                      <div
                        key={index}
                        className="group/item flex items-center justify-between p-4 rounded-2xl bg-slate-50/80 border border-slate-200/50 hover:border-sky-300/50 hover:bg-white transition-all duration-300 hover:translate-x-1 cursor-pointer hover:shadow-md hover:shadow-sky-500/5"
                        style={{ transitionDelay: `${index * 0.05}s` }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-white border border-slate-200/50 flex items-center justify-center shadow-sm overflow-hidden p-1.5 transition-all duration-300 group-hover/item:scale-110 group-hover/item:shadow-lg group-hover/item:border-sky-300/50">
                            <img src={source.logo} alt={`Logo ${source.name} - Source de citations IA`} loading="lazy" className="w-full h-full object-contain transition-transform duration-300 group-hover/item:scale-110" />
                          </div>
                          <div>
                            <div className="font-semibold text-slate-700">{source.name}</div>
                            <div className="text-xs text-slate-400">
                              {source.models.join(", ")}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-2xl font-display font-bold bg-gradient-to-r ${source.color} bg-clip-text text-transparent`}>
                            {source.citations}
                          </div>
                          <div className="text-xs text-slate-400">{t("visibility.top.citations")}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

export default AIVisibility;